import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, BadgeCheck, Building2, ChevronRight, CircleDot, FileCheck, Lock, Monitor, Package, PackageCheck, Printer, Smartphone, Truck,
} from 'lucide-react';

import { loadCollectionActivations, loadCollectionActivity } from './api';
import { countryName } from './countries';
import { tx } from './i18n';
import { codeLabel } from './labels';
import { ownSampleTypeName } from './LaboratoryText';
import { Empty, Failed, Loading, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import {
  CHART, ChartTip, Flag, KpiRow, NUMBER, Panel, Share, ago, counted, hourly, initials, percent, pointLabel, stackRows, tipPoint,
  versusAverage, when,
} from './MobileParts';

// The Collections page's overview, drawn with the Mobile app section's parts: who
// activated kits and how, over a range, by kit and by place, and how far every
// collection has come. The server counts everything; this only draws it.

const text = (key, values) => tx(`ui.collections.${key}`, values);
const LATEST = 6;

// ------------------------------------------------------------------ who activated

// The name a person sees for whoever activated a collection, or why there is none.
export function activatorName(activation) {
  const by = activation.by;
  if (by === null) return text('activation.unrecorded');
  if (by.kind === 'staff') return by.name || by.email;
  if (by.kind === 'clinic') return by.name || text('activation.channel_clinic');
  if (by.status === 'shown') return by.name || by.email || text('activation.app_account');
  if (by.status === 'not_found') return text('activation.account_missing');
  return text('activation.app_account');
}

// Why an app account shows no name, when it shows none.
export function activatorNote(activation) {
  const by = activation.by;
  if (by === null || by.kind !== 'mobile_account') return null;
  if (by.status === 'restricted') return text('activation.account_restricted');
  if (by.status === 'unavailable') return text('activation.account_unavailable', { code: by.code });
  return null;
}

// The portal's avatar: initials for a named person, otherwise why there is no name.
export function ActivatorAvatar({ activation }) {
  const by = activation.by;
  if (by !== null && (by.kind === 'staff' || by.status === 'shown')) {
    return <span className="xo-avatar" aria-hidden="true">{initials(by.name, by.email)}</span>;
  }
  const Icon = by === null ? CircleDot : by.kind === 'clinic' ? Building2 : by.status === 'restricted' ? Lock : Smartphone;
  return <span className="xo-avatar xo-collection-avatar-icon" aria-hidden="true"><Icon /></span>;
}

const CHANNELS = { app: Smartphone, portal: Monitor, clinic: Building2 };

// How a kit was activated. Compact, it is the mark alone, named on hover, as the
// Mobile section marks a phone's platform.
export function ChannelMark({ channel, compact = false }) {
  const Icon = channel === null ? CircleDot : CHANNELS[channel];
  const label = text(channel === null ? 'activation.channel_none' : `activation.channel_${channel}`);
  if (compact) {
    return <span className={`xo-collection-channel is-${channel || 'none'}`} role="img" aria-label={label} title={label}><Icon aria-hidden="true" /></span>;
  }
  return <span className={`xo-collection-channel is-${channel || 'none'}`}><Icon aria-hidden="true" />{label}</span>;
}

// City, region and country as recorded, each named once.
export function placeLabel(place) {
  if (place === null) return null;
  const parts = [place.city, place.region, place.countryCode ? countryName(place.countryCode) : null].filter(Boolean);
  const plain = value => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return parts.filter((part, index) => parts.findIndex(other => plain(other) === plain(part)) === index).join(', ');
}

export function ShortPlace({ place }) {
  if (place === null || (place.city === null && place.countryCode === null)) return null;
  return <span className="xo-collection-place">{place.countryCode && <Flag code={place.countryCode} />}
    {place.city || countryName(place.countryCode)}</span>;
}

// A collection's kit as staff name it: its product, or its sample type when it has none.
export function kitName(collection) {
  return collection.product === null ? ownSampleTypeName(collection.sampleType) : collection.product.name;
}

// ------------------------------------------------------------------ over time

function ActivationChart({ activity }) {
  const hasUnrecorded = activity.series.some(point => point.unrecorded > 0);
  const parts = [
    { key: 'app', name: text('activation.channel_app'), color: CHART.ink },
    { key: 'portal', name: text('activation.channel_portal'), color: CHART.mid },
    ...(hasUnrecorded ? [{ key: 'unrecorded', name: text('activation.channel_none'), color: CHART.soft }] : []),
  ];
  const data = useMemo(() => activity.series.map(point => ({ ...point, total: point.app + point.portal + point.unrecorded })), [activity.series]);
  if (activity.totals.activations === 0) return <Empty title={text('overview.no_activations')} detail={text('overview.no_activations_detail')} />;
  const average = activity.totals.activations / Math.max(1, activity.series.length);
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 14, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(activity.step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={32} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip title={tipPoint(activity.step)} rows={point => stackRows(point, parts)}
              facts={point => [
                { label: tx('ui.mobile.tip_share_of_range'), value: point.total > 0 ? percent(point.total, activity.totals.activations) : null },
                point.total > 0 ? versusAverage(point.total, average) : { note: text('overview.tip_quiet') },
              ]} />
          )} />
          {parts.map((part, index) => <Bar key={part.key} dataKey={part.key} stackId="activations" fill={part.color} maxBarSize={22}
            name={part.name} radius={index === parts.length - 1 ? [3, 3, 0, 0] : undefined} />)}
        </BarChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">
        {parts.map(part => <span key={part.key}><i style={{ background: part.color }} />{part.name}</span>)}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the latest

function LatestActivations({ resource, now, onOpen }) {
  if (resource.loading && resource.data === null) return <Loading label={text('overview.reading_latest')} />;
  if (resource.error && resource.data === null) return <Failed message={resource.error} onRetry={resource.refresh} />;
  const rows = resource.data.activations;
  if (rows.length === 0) return <Empty title={text('overview.no_activations_yet')} />;
  return (
    <ol className="xo-collection-latest">
      {rows.map(collection => {
        const activation = collection.activation;
        return (
          <li key={collection.uuid}>
            <button type="button" onClick={() => onOpen(collection)} aria-label={tx('ui.collections.open_collection', { code: collection.printedCode ?? collection.code })}>
              <ActivatorAvatar activation={activation} />
              <span className="xo-collection-latest-main">
                <strong>{activatorName(activation)}</strong>
                <small><ChannelMark channel={activation.channel} compact /><code>{collection.printedCode ?? collection.code}</code><span>{kitName(collection)}</span></small>
              </span>
              <span className="xo-collection-latest-side">
                <span className="xo-collection-latest-when" title={when(activation.at)}>{ago(activation.at, now)}</span>
                <ShortPlace place={activation.place} />
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ------------------------------------------------------------------ the lifecycle

const STEP_ICONS = { activation: BadgeCheck, transit: Truck, custody: PackageCheck, output: FileCheck };

export function stepLabel(code) {
  if (code === 'created') return tx('ui.collections.created_at');
  if (code === 'printed') return tx('ui.collections.printed');
  return codeLabel('sampleStage', code);
}

function stepIcon(step) {
  if (step.code === 'created') return Package;
  if (step.code === 'printed') return Printer;
  return Object.hasOwn(STEP_ICONS, step.kind) ? STEP_ICONS[step.kind] : CircleDot;
}

function Lifecycle({ lifecycle }) {
  if (lifecycle.total === 0) return <Empty title={tx('ui.collections.empty')} />;
  return (
    <>
      <ol className="xo-collection-lifecycle" style={{ '--xo-collection-steps': lifecycle.steps.length }}>
        {lifecycle.steps.map(step => {
          const Icon = stepIcon(step);
          return (
            <li key={step.code} className={step.reached > 0 ? 'is-reached' : ''}>
              <span className="xo-collection-step-mark" aria-hidden="true"><Icon /></span>
              <span className="xo-collection-step-name">{stepLabel(step.code)}
                {step.source === 'stage' && !step.isActive && <em className="xo-mob-tag">{text('lifecycle.retired')}</em>}</span>
              <strong>{NUMBER.format(step.reached)}</strong>
              <Share value={step.reached} total={lifecycle.total} />
              <small>{text('lifecycle.share', { share: percent(step.reached, lifecycle.total) })}</small>
              <small className="xo-collection-step-now">{counted('ui.collections.lifecycle.now', step.current)}</small>
            </li>
          );
        })}
      </ol>
      {lifecycle.untracked > 0 && <p className="xo-mob-footnote"><AlertTriangle aria-hidden="true" />{counted('ui.collections.lifecycle.untracked', lifecycle.untracked)}</p>}
    </>
  );
}

// ------------------------------------------------------------------ by kit and by place

function Kits({ kits }) {
  if (kits.length === 0) return <Empty title={text('overview.no_kits')} />;
  const most = Math.max(1, ...kits.map(kit => kit.activations));
  return (
    <ol className="xo-mob-rank xo-collection-kits">
      {kits.map(kit => (
        <li key={kit.product.id}>
          <span className="xo-mob-rank-icon xo-collection-kit-art">
            {typeof kit.product.mainImageThumbnailUrl === 'string'
              ? <img src={kit.product.mainImageThumbnailUrl} alt="" loading="lazy" decoding="async" /> : <Package aria-hidden="true" />}
          </span>
          <span className="xo-mob-rank-main">
            <strong>{kit.product.name}</strong>
            <small>{counted('ui.collections.overview.kit_created', kit.created)}</small>
          </span>
          <Share value={kit.activations} total={most} />
          <em>{NUMBER.format(kit.activations)}</em>
        </li>
      ))}
    </ol>
  );
}

function Places({ activity, now }) {
  const [by, setBy] = useState('city');
  const rows = by === 'city'
    ? activity.cities.map(city => ({ key: `${city.city}|${city.countryCode}`, code: city.countryCode, name: city.city,
      detail: `${city.countryCode ? countryName(city.countryCode) : text('overview.country_unknown')} · ${ago(city.latestAt, now)}`,
      activations: city.activations }))
    : activity.countries.map(country => ({ key: country.code, code: country.code, name: countryName(country.code),
      detail: counted('ui.collections.overview.cities_count', country.cities), activations: country.activations }));
  const shown = rows.slice(0, 6);
  const most = Math.max(1, ...shown.map(row => row.activations));
  return (
    <>
      <WorkspaceTabs value={by} onChange={setBy} label={text('overview.places_title')} className="is-compact xo-mob-places-tabs" tabs={[
        { value: 'city', label: text('overview.places_cities') },
        { value: 'country', label: text('overview.places_countries') },
      ]} />
      {shown.length === 0 ? <Empty title={text('overview.no_places')} /> : (
        <ol className="xo-mob-places">
          {shown.map(row => (
            <li key={row.key}>
              <Flag code={row.code} />
              <span><strong>{row.name}</strong><small>{row.detail}</small></span>
              <Share value={row.activations} total={most} />
              <em>{counted('ui.collections.overview.activations_count', row.activations)}</em>
            </li>
          ))}
        </ol>
      )}
      {rows.length > shown.length && <p className="xo-mob-footnote">{counted('ui.collections.overview.more_places', rows.length - shown.length)}</p>}
      {activity.totals.unplaced > 0 && <p className="xo-mob-footnote">{counted('ui.collections.overview.unplaced', activity.totals.unplaced)}</p>}
    </>
  );
}

// ------------------------------------------------------------------ the overview

export default function CollectionOverview({ scope, days, now, onOpen, onShowActivated }) {
  const loadActivity = useCallback(() => loadCollectionActivity({ scope, days }), [scope, days]);
  const loadLatest = useCallback(() => loadCollectionActivations({ scope, limit: LATEST }), [scope]);
  // App activations publish no live signal, so both also refresh every minute.
  const activity = useLiveResource(['collections'], loadActivity, { reconcileMs: 60000 });
  const latest = useLiveResource(['collections'], loadLatest, { reconcileMs: 60000 });

  if (activity.loading && activity.data === null) return <Loading label={text('overview.loading')} />;
  if (activity.error && activity.data === null) return <Failed message={activity.error} onRetry={activity.refresh} />;
  const data = activity.data;
  const totals = data.totals;
  const perPoint = data.series.length > 0 ? totals.activations / data.series.length : 0;

  return (
    <div className="xo-web-page xo-collection-overview">
      {activity.error && <p className="xo-alert t-warn" role="alert">{activity.error}</p>}
      <KpiRow items={[
        { label: text('overview.kpi_activations'), value: NUMBER.format(totals.activations),
          detail: text('overview.kpi_activations_detail', { app: NUMBER.format(totals.app), portal: NUMBER.format(totals.portal) }),
          trend: data.series.map(point => point.app + point.portal + point.unrecorded) },
        { label: text('overview.kpi_created'), value: NUMBER.format(totals.created),
          detail: counted('ui.collections.overview.kpi_created_detail', totals.printed) },
        { label: text('overview.kpi_waiting'), value: NUMBER.format(totals.waiting),
          detail: counted('ui.collections.overview.kpi_waiting_detail', totals.waitingPrinted) },
        { label: text('overview.kpi_countries'), value: NUMBER.format(totals.countries),
          detail: counted('ui.collections.overview.cities_count', totals.cities) },
      ]} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={text('overview.activations_title')}
          detail={text(hourly(data.step) ? 'overview.activations_detail_hours' : 'overview.activations_detail',
            { average: NUMBER.format(Math.round(perPoint * 10) / 10) })}>
          <ActivationChart activity={data} />
        </Panel>
        <Panel title={text('overview.latest_title')} detail={text('overview.latest_detail')} className="xo-collection-latest-panel"
          aside={<button type="button" className="xo-mob-link xo-collection-link" onClick={onShowActivated}>
            {text('overview.all_activated')}<ChevronRight aria-hidden="true" /></button>}>
          <LatestActivations resource={latest} now={now} onOpen={onOpen} />
        </Panel>
      </div>

      <Panel title={text('overview.lifecycle_title')} detail={text('overview.lifecycle_detail')}>
        <Lifecycle lifecycle={data.lifecycle} />
      </Panel>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={text('overview.kits_title')} detail={text('overview.kits_detail')}>
          <Kits kits={data.kits} />
        </Panel>
        <Panel title={text('overview.places_title')} detail={text('overview.places_detail')}>
          <Places activity={data} now={now} />
        </Panel>
      </div>
    </div>
  );
}
